'use client';

/**
 * The workflow canvas — one landscape diagram, the whole workflow in view.
 *
 * WHAT THIS REPLACED
 *
 * Three layout modes: vertical, landscape and flow. Nobody needs to choose an
 * orientation for a process diagram — a process reads left to right, and
 * offering three answers to a question with one right answer made the toolbar
 * busier without making anything clearer. Flow is now the only view.
 *
 * SEEING ALL OF IT
 *
 * The canvas opens at the zoom that fits the whole diagram, capped at 1:1 so a
 * four-step workflow is not blown up to fill a monitor. A 23-step onboarding
 * setup is legible at a glance and unreadable in detail, which is the right
 * trade for the first look — and the reason boxes expand.
 *
 * EXPANSION
 *
 * A box is compact until you hover it or select it. The expanded card is
 * COUNTER-SCALED, so at 40% zoom it stays readable instead of shrinking with
 * everything else. Hover is transient and selection is sticky, because reading
 * one box while comparing it to another needs the first to stay open.
 *
 * THE GRID
 *
 * Off by default, because a grid on an empty canvas is noise. On, it draws
 * lines and snaps every drag, and "Align all" snaps boxes already placed. Steps
 * that have never been moved are laid out automatically and are not saved as
 * positions until somebody moves them — otherwise today's auto-layout gets
 * frozen into the data and no future improvement can reach it.
 *
 * THE LAYOUT IS THE ORDER
 *
 * Dragging a box left of another makes it earlier. That is the only reading
 * anybody brings to a process diagram, and the alternative — a number you can
 * change only in a form — lets the picture and the process disagree while both
 * look right.
 *
 * The renumber this causes is deliberately NOT silent, because an invisible
 * write is what this codebase has repeatedly paid for. A box whose number is
 * about to change shows the new one immediately, struck through against the
 * old; the toolbar says how many steps are affected; and nothing reaches the
 * server until Save is pressed. Rows are banded so three pixels of drag jitter
 * cannot swap two steps that plainly sit side by side.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Crosshair,
  Edit,
  Grid,
  Loader2,
  Maximize2,
  Minus,
  FileText,
  Plus,
  Save,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { WorkflowStep, workflowsAPI } from '@/services/api/workflows';
import { getStepTypeFromConfig } from './StepTypePicker';
import { StepShapePalette, PaletteSelection, SHAPE_DRAG_TYPE } from './StepShapePalette';
import {
  Bounds,
  CanvasShape,
  Edge,
  GRID,
  NODE_H,
  NODE_H_EXPANDED,
  NODE_W,
  Placed,
  bounds,
  edgeAnchors,
  edgePath,
  edges as buildEdges,
  fitScale,
  isBranching,
  layout as buildLayout,
  renumbering,
  shapeForStep,
  snap,
  snapPosition,
} from './canvasGeometry';

const SHAPE_FILL: Record<string, { bg: string; border: string }> = {
  task: { bg: '#dbeafe', border: '#3b82f6' },
  notification: { bg: '#fef3c7', border: '#f59e0b' },
  communication: { bg: '#cffafe', border: '#06b6d4' },
  wait_for_response: { bg: '#fef3c7', border: '#f59e0b' },
  approval: { bg: '#dcfce7', border: '#22c55e' },
  condition: { bg: '#f3e8ff', border: '#a855f7' },
  api_call: { bg: '#ffedd5', border: '#f97316' },
  wait: { bg: '#f3f4f6', border: '#6b7280' },
  form: { bg: '#e0e7ff', border: '#6366f1' },
  billing: { bg: '#fce7f3', border: '#ec4899' },
};

function fillFor(step: WorkflowStep) {
  return SHAPE_FILL[step.step_type] || { bg: '#f3f4f6', border: '#9ca3af' };
}

/** CSS that turns a box into the drawn shape. Clip-path rather than an SVG
 *  node, so the content inside stays ordinary HTML and remains selectable. */
function shapeStyle(shape: CanvasShape): React.CSSProperties {
  switch (shape) {
    case 'diamond':
      return { clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' };
    case 'rounded':
      return { borderRadius: '44px' };
    case 'terminator':
      return { borderRadius: '44px' };
    case 'parallelogram':
      return { clipPath: 'polygon(12% 0%, 100% 0%, 88% 100%, 0% 100%)' };
    case 'note':
      return { clipPath: 'polygon(0 0, 100% 0, 100% 78%, 82% 100%, 0 100%)' };
    default:
      return { borderRadius: '10px' };
  }
}

interface WorkflowCanvasProps {
  steps: WorkflowStep[];
  templateId: number;
  onEdit: (step: WorkflowStep) => void;
  onDelete: (step: WorkflowStep) => void;
  /** Called with where the box was dropped, so a step created from the palette
   *  lands under the cursor rather than at the end of the row. */
  onAddStep: (seed?: { stepTypeValue?: string; canvas_shape?: CanvasShape; position_x?: number; position_y?: number }) => void;
  onSaved?: () => void;
  /** Open the background notes for one step. Linking from the box is the whole
   *  point: somebody looking at a step and wondering why it is there should not
   *  have to switch panes and then say which step they came from. */
  onOpenBackground?: (step: WorkflowStep) => void;
  loading?: boolean;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  steps,
  templateId,
  onEdit,
  onDelete,
  onAddStep,
  onSaved,
  onOpenBackground,
  loading,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  /** The grid stays on until it is turned off — including across reloads and
   *  across opening a different workflow. It is a working preference, and
   *  having to switch it back on every time is what makes people stop using
   *  it. Wrapped because storage throws in a private window. */
  const [showGrid, setShowGrid] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('wf-canvas-grid') === 'on';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem('wf-canvas-grid', showGrid ? 'on' : 'off');
    } catch {
      /* a remembered preference is not worth failing a render over */
    }
  }, [showGrid]);

  /** Guides shown while a box is locked onto a neighbour's edge. */
  const [guides, setGuides] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [armed, setArmed] = useState<PaletteSelection | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Positions being dragged, before they are saved. Keyed by step id so a drag
   *  does not have to round-trip to render. */
  const [draft, setDraft] = useState<Record<number, { x: number; y: number }>>({});


  const placed: Placed[] = useMemo(() => {
    const base = buildLayout(steps);
    return base.map((p) =>
      draft[p.step.id] ? { ...p, x: draft[p.step.id].x, y: draft[p.step.id].y, positioned: true } : p
    );
  }, [steps, draft]);

  /** What each step's number becomes, given where the boxes now sit. Empty
   *  when the arrangement and the order already agree. */
  const renumbered = useMemo(() => renumbering(placed), [placed]);

  /** Something to save: a box was moved, or a move changed a step's number.
   *  Declared after `renumbered` because it reads it — the other order is a
   *  temporal-dead-zone crash on first render, which takes the page down
   *  rather than the button. */
  const dirty = Object.keys(draft).length > 0 || renumbered.size > 0;

  const content: Bounds = useMemo(() => bounds(placed), [placed]);
  const edges: Edge[] = useMemo(() => buildEdges(steps), [steps]);
  const byId = useMemo(() => new Map(placed.map((p) => [p.step.id, p])), [placed]);

  const fit = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const k = fitScale(content, el.clientWidth - 32, el.clientHeight - 32);
    setScale(k);
    // Centre what we just fitted, rather than pinning it to a corner.
    setPan({
      x: (el.clientWidth - content.width * k) / 2 - content.minX * k,
      y: (el.clientHeight - content.height * k) / 2 - content.minY * k,
    });
  }, [content]);

  /** Fit once the viewport has a size and whenever the step count changes.
   *  Not on every `content` change — that would yank the canvas back while
   *  somebody is dragging a box. */
  const stepCount = steps.length;
  useEffect(() => {
    const id = window.requestAnimationFrame(fit);
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepCount, templateId]);

  // ---------------------------------------------------------------- dragging

  /**
   * Moves are tracked on `window`, not on the viewport.
   *
   * The first version called `setPointerCapture` on the event TARGET and read
   * moves off the viewport's React handler. The target is usually a text node
   * inside the box, and selecting the box re-renders it — so the captured
   * element could be replaced mid-gesture, the capture was silently lost, and
   * the box stopped following the cursor after the first few pixels.
   *
   * A window listener is immune to that: it does not care what is under the
   * pointer, what re-rendered, or whether the cursor has left the viewport.
   * The listeners are added on press and removed on release, so nothing is
   * listening while the canvas sits idle.
   */
  const beginGesture = useCallback(
    (
      e: React.PointerEvent,
      onMove: (dx: number, dy: number) => void,
      onEnd?: () => void,
    ) => {
      const startX = e.clientX;
      const startY = e.clientY;

      const move = (ev: PointerEvent) => onMove(ev.clientX - startX, ev.clientY - startY);
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        onEnd?.();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    []
  );

  const onNodePointerDown = (e: React.PointerEvent, p: Placed) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(p.step.id);
    const originX = p.x;
    const originY = p.y;
    // Everything except the box being moved — a box cannot align to itself.
    const neighbours = placed
      .filter((o) => o.step.id !== p.step.id)
      .map((o) => ({ x: o.x, y: o.y, height: NODE_H }));

    beginGesture(
      e,
      (dx, dy) => {
        // Divided by scale: a 10px cursor move at 50% zoom is 20 diagram units,
        // and without this the box lags the pointer the further you zoom out.
        const nx = originX + dx / scale;
        const ny = originY + dy / scale;
        const hit = snapPosition(nx, ny, neighbours, showGrid);
        setGuides({ x: hit.guideX, y: hit.guideY });
        setDraft((prev) => ({ ...prev, [p.step.id]: { x: hit.x, y: hit.y } }));
      },
      () => setGuides({ x: null, y: null })
    );
  };

  const onCanvasPointerDown = (e: React.PointerEvent) => {
    if (armed) return; // the next click places a shape, not a pan
    setSelectedId(null);
    const originX = pan.x;
    const originY = pan.y;
    beginGesture(e, (dx, dy) => setPan({ x: originX + dx, y: originY + dy }));
  };

  /** Where in diagram units a viewport click landed. */
  const toDiagram = (clientX: number, clientY: number) => {
    const el = viewportRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: Math.round((clientX - r.left - pan.x) / scale),
      y: Math.round((clientY - r.top - pan.y) / scale),
    };
  };

  /** Place a new shape, from a click or from a drop, and open its editor.
   *
   *  The position is snapped the same way a dragged box is, so a shape dropped
   *  next to an existing one lines up with it immediately rather than sitting
   *  a few units off until somebody nudges it. */
  const placeAt = useCallback(
    (clientX: number, clientY: number, selection: PaletteSelection) => {
      const at = toDiagram(clientX, clientY);
      const hit = snapPosition(
        at.x - NODE_W / 2,
        at.y - NODE_H / 2,
        placed.map((o) => ({ x: o.x, y: o.y, height: NODE_H })),
        showGrid
      );
      onAddStep({
        stepTypeValue: selection.stepTypeValue,
        canvas_shape: selection.shape,
        position_x: hit.x,
        position_y: hit.y,
      });
      setArmed(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placed, showGrid, scale, pan, onAddStep]
  );

  const onCanvasClick = (e: React.MouseEvent) => {
    if (!armed) return;
    placeAt(e.clientX, e.clientY, armed);
  };

  /** A shape dragged out of the palette and released over the canvas. */
  const onCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData(SHAPE_DRAG_TYPE);
    if (!raw) return;
    try {
      placeAt(e.clientX, e.clientY, JSON.parse(raw) as PaletteSelection);
    } catch {
      /* a drop carrying something else is not ours to act on */
    }
  };

  // ------------------------------------------------------------------ saving

  const save = async () => {
    // Every box that moved, plus every box whose NUMBER changed because
    // something else moved past it. A step that did not move can still be
    // renumbered, and leaving it out would save an order the diagram does not
    // show.
    const ids = new Set<number>([
      ...Object.keys(draft).map(Number),
      ...renumbered.keys(),
    ]);
    const nodes = [...ids].map((id) => {
      const p = byId.get(id);
      return {
        step_id: id,
        position_x: draft[id]?.x ?? p?.x ?? 0,
        position_y: draft[id]?.y ?? p?.y ?? 0,
        step_order: renumbered.get(id),
      };
    });
    if (!nodes.length) return;
    setSaving(true);
    setError(null);
    try {
      const result = await workflowsAPI.saveCanvasLayout(templateId, nodes);
      if (result.rejected.length) {
        // Reported rather than swallowed: ids the server refused mean the
        // canvas and the server disagree about what this workflow contains,
        // and silently dropping them would leave boxes that jump back on
        // reload with nothing said.
        setError(
          `${result.rejected.length} step(s) could not be saved — reload the workflow.`
        );
      }
      setDraft({});
      onSaved?.();
    } catch {
      setError('The layout could not be saved. Your arrangement is still on screen; try again.');
    } finally {
      setSaving(false);
    }
  };

  const alignAll = () => {
    setShowGrid(true);
    const next: Record<number, { x: number; y: number }> = {};
    placed.forEach((p) => {
      const x = snap(p.x);
      const y = snap(p.y);
      // Every box, not only the ones already moved: "Align all" that left the
      // auto-placed ones alone would look like it had half worked.
      if (x !== p.x || y !== p.y || !p.positioned) next[p.step.id] = { x, y };
    });
    setDraft((prev) => ({ ...prev, ...next }));
  };

  // ------------------------------------------------------------------ render

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#6b7280' }} />
      </div>
    );
  }

  const toolBtn: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: 12,
    border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer', color: '#374151',
  };

  return (
    <div style={{ display: 'flex', gap: 12, height: '70vh', minHeight: 460 }}>
      <StepShapePalette armed={armed} onArm={setArmed} />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={toolBtn} onClick={fit} title="Fit the whole workflow in view">
            <Maximize2 className="w-3.5 h-3.5" /> Fit
          </button>
          <button type="button" style={toolBtn} onClick={() => setScale((s) => Math.min(2, s + 0.1))} title="Zoom in">
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
          <button type="button" style={toolBtn} onClick={() => setScale((s) => Math.max(0.2, s - 0.1))} title="Zoom out">
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span style={{ fontSize: 12, color: '#6b7280', minWidth: 44 }}>{Math.round(scale * 100)}%</span>

          <button
            type="button"
            style={{ ...toolBtn, background: showGrid ? '#111827' : 'white', color: showGrid ? 'white' : '#374151' }}
            onClick={() => setShowGrid((g) => !g)}
            title="Show a grid and snap boxes to it"
          >
            <Grid className="w-3.5 h-3.5" /> Grid
          </button>
          <button type="button" style={toolBtn} onClick={alignAll} title="Snap every box to the grid">
            <Crosshair className="w-3.5 h-3.5" /> Align all
          </button>

          <div style={{ flex: 1 }} />

          {dirty && (
            <button
              type="button"
              style={{ ...toolBtn, background: '#10b981', color: 'white', borderColor: '#10b981', fontWeight: 600 }}
              onClick={() => void save()}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {renumbered.size ? 'Save layout & order' : 'Save layout'}
            </button>
          )}
          <button type="button" style={toolBtn} onClick={() => onAddStep()}>
            <Plus className="w-3.5 h-3.5" /> Add step
          </button>
        </div>

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 12,
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b',
          }}>
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {renumbered.size > 0 && (
          <div style={{
            padding: '8px 12px', fontSize: 12, background: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: 6, color: '#92400e',
          }}>
            {renumbered.size} step{renumbered.size === 1 ? '' : 's'} will be renumbered to match the
            layout. Nothing is saved until you press Save.
          </div>
        )}

        {armed && (
          <div style={{
            padding: '8px 12px', fontSize: 12, background: '#eff6ff',
            border: '1px solid #bfdbfe', borderRadius: 6, color: '#1e40af',
          }}>
            Click the canvas to place <strong>{armed.label}</strong>. Press Escape to cancel.
          </div>
        )}

        {/* Viewport */}
        <div
          ref={viewportRef}
          onPointerDown={onCanvasPointerDown}
          onClick={onCanvasClick}
          onDragOver={(e) => {
            // Without preventDefault the browser refuses the drop outright and
            // the shape springs back to the palette with no explanation.
            if (e.dataTransfer.types.includes(SHAPE_DRAG_TYPE)) e.preventDefault();
          }}
          onDrop={onCanvasDrop}
          onKeyDown={(e) => e.key === 'Escape' && setArmed(null)}
          tabIndex={0}
          style={{
            position: 'relative', flex: 1, overflow: 'hidden', background: '#fafafa',
            border: '1px solid #e5e7eb', borderRadius: 10,
            cursor: armed ? 'crosshair' : 'grab',
            // The grid is drawn on the viewport and scaled with the content, so
            // a snapped box sits on a line at every zoom.
            backgroundImage: showGrid
              ? `linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)`
              : undefined,
            backgroundSize: showGrid ? `${GRID * scale}px ${GRID * scale}px` : undefined,
            backgroundPosition: showGrid ? `${pan.x}px ${pan.y}px` : undefined,
          }}
        >
          <div style={{ position: 'absolute', transformOrigin: '0 0', transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}>
            {/* Edges behind the boxes */}
            <svg
              style={{ position: 'absolute', overflow: 'visible', pointerEvents: 'none' }}
              width={Math.max(1, content.maxX)}
              height={Math.max(1, content.maxY)}
            >
              <defs>
                <marker id="wf-arrow" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L9,3 z" fill="#9ca3af" />
                </marker>
              </defs>
              {edges.map((e, i) => {
                const a = byId.get(e.from);
                const b = byId.get(e.to);
                if (!a || !b) return null;
                // An expanded box is taller, and an edge aimed at the collapsed
                // centre visibly misses the box it points at.
                const heightOf = (id: number) =>
                  selectedId === id || hoveredId === id ? NODE_H_EXPANDED : NODE_H;
                const k = edgeAnchors(a, b, heightOf(e.from), heightOf(e.to));
                const stroke = e.tone === 'yes' ? '#22c55e' : e.tone === 'no' ? '#ef4444' : '#9ca3af';
                return (
                  <g key={`${e.from}-${e.to}-${i}`}>
                    <path
                      d={edgePath(k)}
                      fill="none"
                      stroke={stroke}
                      strokeWidth={2}
                      markerEnd="url(#wf-arrow)"
                    />
                    {e.label && (
                      <text
                        x={k.labelX}
                        y={k.labelY}
                        fill={stroke}
                        fontSize={11}
                        fontWeight={600}
                        textAnchor="middle"
                        /* Painted over the line, so a label never reads as part
                           of the path it sits on. */
                        paintOrder="stroke"
                        stroke="#fafafa"
                        strokeWidth={3}
                      >
                        {e.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Alignment guides — only while a box is locked onto a neighbour.
                Drawn across the whole diagram so it is obvious WHICH edge it
                has caught, not merely that something snapped. */}
            {(guides.x !== null || guides.y !== null) && (
              <svg
                style={{ position: 'absolute', overflow: 'visible', pointerEvents: 'none' }}
                width={Math.max(1, content.maxX)}
                height={Math.max(1, content.maxY)}
              >
                {guides.x !== null && (
                  <line
                    x1={guides.x}
                    y1={content.minY}
                    x2={guides.x}
                    y2={content.maxY}
                    stroke="#ec4899"
                    strokeWidth={1}
                    strokeDasharray="6 4"
                  />
                )}
                {guides.y !== null && (
                  <line
                    x1={content.minX}
                    y1={guides.y}
                    x2={content.maxX}
                    y2={guides.y}
                    stroke="#ec4899"
                    strokeWidth={1}
                    strokeDasharray="6 4"
                  />
                )}
              </svg>
            )}

            {/* Boxes */}
            {placed.map((p) => {
              const expanded = selectedId === p.step.id || hoveredId === p.step.id;
              const shape = shapeForStep(p.step);
              const colour = fillFor(p.step);
              const option = getStepTypeFromConfig(p.step.step_type, p.step.step_config);
              return (
                <div
                  key={p.step.id}
                  onPointerDown={(e) => onNodePointerDown(e, p)}
                  onMouseEnter={() => setHoveredId(p.step.id)}
                  onMouseLeave={() => setHoveredId((h) => (h === p.step.id ? null : h))}
                  style={{
                    position: 'absolute', left: p.x, top: p.y, width: NODE_W,
                    height: expanded ? NODE_H_EXPANDED : NODE_H,
                    background: colour.bg,
                    border: `2px solid ${selectedId === p.step.id ? '#111827' : colour.border}`,
                    boxShadow: expanded ? '0 8px 24px rgba(0,0,0,0.18)' : '0 1px 3px rgba(0,0,0,0.08)',
                    cursor: 'move', transition: 'height .12s ease, box-shadow .12s ease',
                    zIndex: expanded ? 20 : 1, overflow: 'hidden',
                    // An auto-placed box is drawn dashed, so "the canvas put
                    // this here" is visibly different from "somebody put this
                    // here" — and it is obvious what Save layout will fix.
                    borderStyle: p.positioned ? 'solid' : 'dashed',
                    ...shapeStyle(shape),
                  }}
                >
                  <div style={{
                    padding: shape === 'diamond' ? '18px 34px' : '10px 12px',
                    height: '100%', display: 'flex', flexDirection: 'column',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: '#6b7280' }}>
                      {renumbered.has(p.step.id) ? (
                        /* Old struck through, new beside it. Showing only the
                           new number would be the silent renumber wearing a
                           different hat: you could not tell what changed. */
                        <span style={{ fontWeight: 700 }}>
                          <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>
                            {p.step.step_order}
                          </span>{' '}
                          <span style={{ color: '#b45309' }}>{renumbered.get(p.step.id)}</span>
                        </span>
                      ) : (
                        <span style={{ fontWeight: 700 }}>{p.step.step_order}</span>
                      )}
                      <span style={{ textTransform: 'uppercase', letterSpacing: .4 }}>
                        {option?.label || p.step.step_type.replace(/_/g, ' ')}
                      </span>
                      {isBranching(p.step) && <span style={{ color: '#a855f7', fontWeight: 700 }}>◆</span>}
                    </div>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#111827', marginTop: 4,
                      lineHeight: 1.25, overflow: 'hidden',
                      display: '-webkit-box', WebkitLineClamp: expanded ? 3 : 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {p.step.step_name}
                    </div>

                    {expanded && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#4b5563', flex: 1, overflow: 'hidden' }}>
                        {p.step.step_group && (
                          <div style={{ marginBottom: 4, color: '#6b7280' }}>{p.step.step_group}</div>
                        )}
                        {p.step.default_assignee_role && <div>Role: {p.step.default_assignee_role}</div>}
                        {p.step.page_key && <div>Page: {p.step.page_key}</div>}
                        {p.step.location_hint && <div>{p.step.location_hint}</div>}
                        {p.step.estimated_duration_hours != null && (
                          <div>{p.step.estimated_duration_hours}h</div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button
                            type="button"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onEdit(p.step); }}
                            style={{ ...toolBtn, padding: '4px 8px', fontSize: 11 }}
                          >
                            <Edit className="w-3 h-3" /> Edit
                          </button>
                          {onOpenBackground && (
                            <button
                              type="button"
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); onOpenBackground(p.step); }}
                              title="Meetings, decisions and constraints behind this step"
                              style={{ ...toolBtn, padding: '4px 8px', fontSize: 11 }}
                            >
                              <FileText className="w-3 h-3" /> Why
                            </button>
                          )}
                          <button
                            type="button"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onDelete(p.step); }}
                            style={{ ...toolBtn, padding: '4px 8px', fontSize: 11, color: '#b91c1c' }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {!placed.length && (
              <div style={{ position: 'absolute', left: 40, top: 40, color: '#6b7280', fontSize: 13 }}>
                No steps yet — pick a shape from the palette and click here, or use Add step.
              </div>
            )}
          </div>
        </div>

        <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
          Drag a box to move it. Steps run in the order they read on the canvas — left to right, then
          down — so moving a box before another makes it happen earlier.
        </p>
      </div>
    </div>
  );
};

export default WorkflowCanvas;
